import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";
import Login from "@/pages/Login";
import SystemStatus from "@/pages/SystemStatus";
import Home from "@/pages/Home";
import SetupYear from "@/pages/district/SetupYear";
import AllocationManager from "@/pages/district/AllocationManager";
import ClubReview from "@/pages/district/ClubReview";
import ClubGov from "@/pages/ClubGov";
import ClubPrep from "@/pages/ClubPrep";
import ClubDashboard from "@/pages/ClubDashboard";
import AccountSecurity from "@/pages/AccountSecurity";
import Directory from "@/pages/Directory";
import MyProfile from "@/pages/MyProfile";
import ClubBoard from "@/pages/ClubBoard";
import ClubConstitute from "@/pages/ClubConstitute";
import ClubMembers from "@/pages/ClubMembers";
import GovOps from "@/pages/GovOps";
import ConfigEditor from "@/pages/ConfigEditor";
import VisitDetail from "@/pages/VisitDetail";
import InvitationLanding from "@/pages/InvitationLanding";
import Onboard from "@/pages/Onboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import MobileAccess from "@/pages/MobileAccess";
import MobileSetup from "@/pages/MobileSetup";
import Unlock from "@/pages/Unlock";
import PinReset from "@/pages/PinReset";
import DevicesAccess from "@/pages/DevicesAccess";
import Support from "@/pages/Support";
import NotFound from "@/pages/NotFound";
import "@/App.css";

function RootEntry() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Stable onboarding links land on the app root with ?join=<token>; forward into the public
  // activation flow before the protected route can bounce to /login and drop the token.
  if (params.get("join")) return <Navigate to={`/onboard${location.search}`} replace />;
  return (
    <ProtectedRoute>
      <AppShell>
        <Home />
      </AppShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Public access-lifecycle pages (token relayed via HttpOnly cookies only) */}
            <Route path="/onboard" element={<Onboard />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/mobile-setup" element={<MobileSetup />} />
            <Route path="/unlock" element={<Unlock />} />
            <Route path="/pin-reset" element={<PinReset />} />
            <Route
              path="/mobile-access"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <MobileAccess />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/devices"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <DevicesAccess />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/status"
              element={
                <ProtectedRoute>
                  <SystemStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Support />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={<RootEntry />}
            />
            <Route
              path="/gov/clubs/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubGov />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            {/* ---- Fresh district-side V1 ---- */}
            <Route path="/setup" element={<ProtectedRoute><AppShell><SetupYear /></AppShell></ProtectedRoute>} />
            <Route path="/setup/allocation" element={<ProtectedRoute><AppShell><AllocationManager /></AppShell></ProtectedRoute>} />
            <Route path="/visits" element={<Navigate to="/" replace />} />
            <Route path="/review/:clubId" element={<ProtectedRoute><AppShell><ClubReview /></AppShell></ProtectedRoute>} />
            {/* Old district screens redirect into the fresh flow */}
            <Route path="/admin/setup" element={<Navigate to="/setup" replace />} />
            <Route path="/admin/team-setup" element={<Navigate to="/setup" replace />} />
            <Route path="/admin/district-members" element={<Navigate to="/setup" replace />} />
            <Route path="/gov/ops" element={<Navigate to="/visits" replace />} />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <AccountSecurity />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gov"
              element={<Navigate to="/visits" replace />}
            />
            <Route
              path="/gov/ops-legacy"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <GovOps />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gov/config/:id"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ConfigEditor />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gov/visits/:id"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <VisitDetail />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            {/* Public: invitation landing must run BEFORE auth to preserve the BFF login round-trip */}
            <Route path="/gov/invitation" element={<InvitationLanding />} />
            <Route
              path="/directory"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Directory />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clubs"
              element={<Navigate to="/visits" replace />}
            />
            <Route
              path="/club/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubDashboard />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/prep"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubPrep />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/prep/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubPrep />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <MyProfile />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/board"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubConstitute mode="board" />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/committees"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubConstitute mode="committee" />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubMembers />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/board/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubConstitute mode="board" />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/committees/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubConstitute mode="committee" />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/members/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubMembers />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/directory/:clubId"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <Directory />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/board-legacy"
              element={
                <ProtectedRoute>
                  <AppShell>
                    <ClubBoard />
                  </AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/imports" element={<Navigate to="/setup" replace />} />
            {/* Disabled module — resolves to a safe 404 */}
            <Route path="/club" element={<NotFound />} />
            <Route path="/club/*" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
